import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  FiTrendingUp,
  FiFilter,
  FiDollarSign,
  FiCreditCard,
  FiGrid,
  FiClock,
  FiStar,
  FiInfo,
  FiDownload
} from 'react-icons/fi';

const PAYMENT_METHODS = [
  { key: 'cash', label: 'Cash', color: '#111827' },
  { key: 'card', label: 'Card', color: '#374151' },
  { key: 'upi', label: 'UPI', color: '#6b7280' },
  { key: 'wallet', label: 'Wallet', color: '#9ca3af' },
  { key: 'unknown', label: 'Other', color: '#d1d5db' }
];

const InfoTooltip = ({ text }) => (
  <span className="relative inline-flex items-center group">
    <FiInfo className="text-gray-400" />
    <span className="pointer-events-none absolute -top-2 left-1/2 z-20 hidden -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg border border-gray-200 bg-white px-2 py-1 text-[11px] text-gray-700 shadow-md group-hover:block">
      {text}
    </span>
  </span>
);

function AdvancedAnalytics() {
  const [bills, setBills] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [waitlistStats, setWaitlistStats] = useState(null);
  const [feedbackAnalytics, setFeedbackAnalytics] = useState(null);
  const [kdsAnalytics, setKdsAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [hoveredHeatmapCell, setHoveredHeatmapCell] = useState(null);
  const [paymentFilters, setPaymentFilters] = useState(PAYMENT_METHODS.map(m => m.key));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchBaseData();
  }, []);

  useEffect(() => {
    fetchFeedbackAnalytics();
    fetchReservations();
    fetchKdsAnalytics();
  }, [startDate, endDate]);

  const fetchBaseData = async () => {
    try {
      setLoading(true);
      const [billsRes, ordersRes, tablesRes, menuRes, waitlistRes] = await Promise.all([
        api.get('/api/bills').catch(() => ({ data: { bills: [] } })),
        api.get('/api/pos/orders/active').catch(() => ({ data: { orders: [] } })),
        api.get('/api/tables').catch(() => ({ data: [] })),
        api.get('/api/menu').catch(() => ({ data: { items: [] } })),
        api.get('/api/waitlist/stats').catch(() => ({ data: null }))
      ]);

      setBills(billsRes.data?.bills || []);
      setOrders(ordersRes.data?.orders || []);
      setTables(Array.isArray(tablesRes.data) ? tablesRes.data : []);
      setMenuItems(menuRes.data?.items || []);
      setWaitlistStats(waitlistRes.data || null);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReservations = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('date', startDate);
      const response = await api.get(`/api/reservations?${params.toString()}`);
      setReservations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
      setReservations([]);
    }
  };

  const fetchFeedbackAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('date_from', `${startDate}T00:00:00Z`);
      if (endDate) params.append('date_to', `${endDate}T23:59:59Z`);
      const response = await api.get(`/api/feedback/analytics?${params.toString()}`);
      setFeedbackAnalytics(response.data || null);
    } catch (error) {
      console.error('Failed to fetch feedback analytics:', error);
      setFeedbackAnalytics(null);
    }
  };

  const fetchKdsAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('date_from', `${startDate}T00:00:00Z`);
      if (endDate) params.append('date_to', `${endDate}T23:59:59Z`);
      const response = await api.get(`/api/kds/analytics?${params.toString()}`);
      setKdsAnalytics(response.data?.items || []);
    } catch (error) {
      console.error('Failed to fetch KDS analytics:', error);
      setKdsAnalytics([]);
    }
  };

  const normalizeDate = (dateString) => {
    if (!dateString) return null;
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.match(/-\d{2}:\d{2}$/)) {
      return new Date(`${dateString}Z`);
    }
    return new Date(dateString);
  };

  const formatHourLabel = (hour) => {
    const hourNumber = Number(hour);
    if (Number.isNaN(hourNumber)) return '';
    const suffix = hourNumber >= 12 ? 'PM' : 'AM';
    const display = hourNumber % 12 === 0 ? 12 : hourNumber % 12;
    return `${display}${suffix}`;
  };

  const normalizePaymentMethod = (value) => {
    const method = String(value || '').trim().toLowerCase();
    if (!method) return 'unknown';

    if (method.includes('cash')) return 'cash';
    if (method.includes('card') || method.includes('credit') || method.includes('debit')) return 'card';
    if (method.includes('upi') || method.includes('gpay') || method.includes('phonepe') || method.includes('paytm')) return 'upi';
    if (method.includes('wallet')) return 'wallet';

    return 'unknown';
  };

  const filteredBills = useMemo(() => {
    return bills.filter((bill) => {
      const billDate = normalizeDate(bill.created_at);
      if (!billDate || Number.isNaN(billDate.getTime())) return false;

      const start = startDate ? new Date(`${startDate}T00:00:00.000Z`) : null;
      const end = endDate ? new Date(`${endDate}T23:59:59.999Z`) : null;
      const inRange = (!start || billDate >= start) && (!end || billDate <= end);

      const normalizedMethod = normalizePaymentMethod(bill.payment_method);
      const inPayment = paymentFilters.length === 0 || paymentFilters.includes(normalizedMethod);

      return inRange && inPayment;
    });
  }, [bills, startDate, endDate, paymentFilters]);

  const totals = useMemo(() => {
    const totalRevenue = filteredBills.reduce((sum, bill) => sum + parseFloat(bill.total_amount || 0), 0);
    const taxCollected = filteredBills.reduce((sum, bill) => sum + parseFloat(bill.tax_amount || 0), 0);
    const serviceCharge = filteredBills.reduce((sum, bill) => sum + parseFloat(bill.service_charge || 0), 0);
    const discounts = filteredBills.reduce((sum, bill) => sum + parseFloat(bill.discount_amount || 0), 0);
    const totalBills = filteredBills.length;
    const avgBill = totalBills > 0 ? totalRevenue / totalBills : 0;

    return { totalRevenue, taxCollected, serviceCharge, discounts, totalBills, avgBill };
  }, [filteredBills]);

  const revenueTrend = useMemo(() => {
    const data = [];
    const rangeStart = startDate ? new Date(`${startDate}T00:00:00`) : new Date(Date.now() - 6 * 86400000);
    const rangeEnd = endDate ? new Date(`${endDate}T00:00:00`) : new Date();

    const days = Math.max(1, Math.min(31, Math.ceil((rangeEnd - rangeStart) / 86400000) + 1));

    for (let i = 0; i < days; i++) {
      const date = new Date(rangeStart);
      date.setDate(rangeStart.getDate() + i);
      const key = date.toISOString().split('T')[0];

      const dayRevenue = filteredBills
        .filter(bill => {
          const billDate = normalizeDate(bill.created_at);
          if (!billDate) return false;
          return billDate.toISOString().split('T')[0] === key;
        })
        .reduce((sum, bill) => sum + parseFloat(bill.total_amount || 0), 0);

      data.push({
        date: key,
        label: date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
        revenue: dayRevenue
      });
    }

    return data;
  }, [filteredBills, startDate, endDate]);

  const paymentBreakdown = useMemo(() => {
    const map = {};
    PAYMENT_METHODS.forEach(method => {
      map[method.key] = { method: method.label, revenue: 0, count: 0, color: method.color };
    });

    filteredBills.forEach(bill => {
      const methodKey = normalizePaymentMethod(bill.payment_method);
      const bucket = map[methodKey] || map.unknown;
      bucket.revenue += parseFloat(bill.total_amount || 0);
      bucket.count += 1;
    });

    return Object.values(map).filter(item => item.count > 0 || item.revenue > 0);
  }, [filteredBills]);

  const diningMetrics = useMemo(() => {
    const durations = [];
    const tableBillMap = new Map();

    filteredBills.forEach(bill => {
      const order = bill.orders || {};
      const createdAt = normalizeDate(order.created_at || bill.created_at);
      const completedAt = normalizeDate(order.completed_at || bill.created_at);
      if (createdAt && completedAt && !Number.isNaN(createdAt.getTime()) && !Number.isNaN(completedAt.getTime())) {
        const minutes = Math.max(0, (completedAt - createdAt) / 60000);
        durations.push(minutes);
      }

      const tableNumber = order.tables?.table_number || bill.orders?.tables?.table_number;
      const billTime = normalizeDate(bill.created_at);
      if (tableNumber && billTime && !Number.isNaN(billTime.getTime())) {
        if (!tableBillMap.has(tableNumber)) {
          tableBillMap.set(tableNumber, []);
        }
        tableBillMap.get(tableNumber).push(billTime);
      }
    });

    const avgDining = durations.length > 0
      ? durations.reduce((sum, value) => sum + value, 0) / durations.length
      : 0;

    const turnoverDurations = [];
    tableBillMap.forEach(times => {
      const sorted = times.sort((a, b) => a - b);
      for (let i = 1; i < sorted.length; i++) {
        const diff = (sorted[i] - sorted[i - 1]) / 60000;
        if (diff >= 0) turnoverDurations.push(diff);
      }
    });

    const avgTurnover = turnoverDurations.length > 0
      ? turnoverDurations.reduce((sum, value) => sum + value, 0) / turnoverDurations.length
      : 0;

    return {
      avgDiningMinutes: avgDining,
      avgTurnoverMinutes: avgTurnover,
      diningSamples: durations.length,
      turnoverSamples: turnoverDurations.length
    };
  }, [filteredBills]);

  const peakHeatmap = useMemo(() => {
    const grid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));

    filteredBills.forEach(bill => {
      const billDate = normalizeDate(bill.created_at);
      if (!billDate || Number.isNaN(billDate.getTime())) return;

      const hour = parseInt(billDate.toLocaleString('en-IN', { hour: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }), 10);
      const dayIndex = billDate.getDay();
      if (!Number.isNaN(dayIndex) && !Number.isNaN(hour)) {
        grid[dayIndex][hour] += 1;
      }
    });

    const maxValue = Math.max(1, ...grid.flat());

    return { grid, maxValue };
  }, [filteredBills]);

  const kdsPrepTrend = useMemo(() => {
    const map = new Map();
    let totalMinutes = 0;
    let sampleCount = 0;

    kdsAnalytics.forEach(entry => {
      const displayTime = normalizeDate(entry.display_time);
      const readyTime = normalizeDate(entry.ready_time);
      if (!displayTime || !readyTime) return;
      const minutes = Math.max(0, (readyTime - displayTime) / 60000);
      totalMinutes += minutes;
      sampleCount += 1;

      const key = displayTime.toISOString().split('T')[0];
      if (!map.has(key)) {
        map.set(key, { date: key, label: new Date(key).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }), total: 0, count: 0 });
      }
      const entryData = map.get(key);
      entryData.total += minutes;
      entryData.count += 1;
    });

    const trend = Array.from(map.values())
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map(item => ({
        label: item.label,
        date: item.date,
        avgMinutes: item.count > 0 ? item.total / item.count : 0
      }));

    return {
      trend,
      avgPrepMinutes: sampleCount > 0 ? totalMinutes / sampleCount : 0,
      samples: sampleCount
    };
  }, [kdsAnalytics]);

  const hourlyRevenue = useMemo(() => {
    const buckets = Array.from({ length: 24 }, (_, hour) => ({ hour, revenue: 0 }));

    filteredBills.forEach(bill => {
      const billDate = normalizeDate(bill.created_at);
      if (!billDate) return;
      const istHour = parseInt(billDate.toLocaleString('en-IN', { hour: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }), 10);
      if (!Number.isNaN(istHour)) {
        buckets[istHour].revenue += parseFloat(bill.total_amount || 0);
      }
    });

    return buckets.map(bucket => ({
      hour: `${bucket.hour}:00`,
      revenue: bucket.revenue
    }));
  }, [filteredBills]);

  const topItems = useMemo(() => {
    const itemMap = new Map();

    filteredBills.forEach(bill => {
      const items = bill.orders?.order_items || [];
      items.forEach(item => {
        const name = item.menu_items?.name || 'Item';
        const quantity = parseFloat(item.quantity || 0);
        const subtotal = parseFloat(item.subtotal || 0);

        if (!itemMap.has(name)) {
          itemMap.set(name, { name, quantity: 0, revenue: 0 });
        }
        const current = itemMap.get(name);
        current.quantity += quantity;
        current.revenue += subtotal;
      });
    });

    return Array.from(itemMap.values())
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 8);
  }, [filteredBills]);

  const orderStatus = useMemo(() => {
    const statusCounts = {
      pending: 0,
      preparing: 0,
      ready: 0,
      completed: 0
    };

    orders.forEach(order => {
      const status = order.status || 'pending';
      if (statusCounts[status] !== undefined) {
        statusCounts[status] += 1;
      }
    });

    return [
      { name: 'Pending', value: statusCounts.pending, color: '#9ca3af' },
      { name: 'Preparing', value: statusCounts.preparing, color: '#6b7280' },
      { name: 'Ready', value: statusCounts.ready, color: '#4b5563' },
      { name: 'Completed', value: statusCounts.completed, color: '#111827' }
    ].filter(item => item.value > 0);
  }, [orders]);

  const tableStatus = useMemo(() => {
    const counts = { available: 0, occupied: 0, reserved: 0, cleaning: 0 };
    tables.forEach(table => {
      const status = table.status || 'available';
      if (counts[status] !== undefined) {
        counts[status] += 1;
      }
    });
    return counts;
  }, [tables]);

  const reservationStats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const todayReservations = reservations.filter(r => (r.reservation_date || '').startsWith(today));
    const statusCounts = todayReservations.reduce((acc, r) => {
      const status = r.status || 'pending';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return {
      totalToday: todayReservations.length,
      statusCounts
    };
  }, [reservations]);

  const togglePaymentFilter = (key) => {
    setPaymentFilters((prev) => {
      if (prev.includes(key)) {
        return prev.filter(item => item !== key);
      }
      return [...prev, key];
    });
  };

  const handleDownloadReport = async () => {
    try {
      setDownloadingReport(true);
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get('/api/analytics/report/pdf', {
        params,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics-report-${startDate || 'from'}_to_${endDate || 'to'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      let message = 'Failed to download report PDF. Please try again.';
      const status = error?.response?.status;
      const responseData = error?.response?.data;

      if (responseData instanceof Blob) {
        try {
          const text = await responseData.text();
          const parsed = JSON.parse(text);
          message = parsed?.error || parsed?.message || message;
        } catch (_) {
          // ignore parse errors and keep fallback message
        }
      } else if (responseData?.error || responseData?.message) {
        message = responseData.error || responseData.message;
      }

      console.error('Failed to download analytics report:', {
        status,
        message,
        error
      });
      alert(message);
    } finally {
      setDownloadingReport(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Advanced Analytics">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Advanced Analytics">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Advanced Analytics</h1>
          <p className="text-sm text-gray-600">Revenue intelligence, bill insights, and operational health</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
            <div className="flex items-center gap-2 text-gray-700 font-medium">
              <FiFilter className="text-base" />
              Filters
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
                />
              </div>
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setPaymentFilters(PAYMENT_METHODS.map(m => m.key));
                }}
                className="px-3 py-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Reset Filters
              </button>
              <button
                onClick={handleDownloadReport}
                disabled={downloadingReport}
                className="px-3 py-2 text-xs font-semibold text-white bg-gray-900 border border-gray-900 rounded-lg hover:bg-gray-800 inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <FiDownload className="text-sm" />
                {downloadingReport ? 'Generating...' : 'Get Reports (PDF)'}
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {PAYMENT_METHODS.map(method => (
              <button
                key={method.key}
                onClick={() => togglePaymentFilter(method.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  paymentFilters.includes(method.key)
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {method.label}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                Total Revenue
                <InfoTooltip text="Sum of bill totals in the selected range." />
              </span>
              <FiTrendingUp className="text-gray-400" />
            </div>
            <div className="text-xl font-bold text-gray-900">₹{totals.totalRevenue.toFixed(2)}</div>
            <p className="text-xs text-gray-500 mt-1">{totals.totalBills} bills</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                Average Bill
                <InfoTooltip text="Average bill total across all bills in range." />
              </span>
              <FiDollarSign className="text-gray-400" />
            </div>
            <div className="text-xl font-bold text-gray-900">₹{totals.avgBill.toFixed(2)}</div>
            <p className="text-xs text-gray-500 mt-1">per bill</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                Tax Collected
                <InfoTooltip text="Total CGST + SGST from bills in range." />
              </span>
              <FiCreditCard className="text-gray-400" />
            </div>
            <div className="text-xl font-bold text-gray-900">₹{totals.taxCollected.toFixed(2)}</div>
            <p className="text-xs text-gray-500 mt-1">CGST + SGST</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                Service Charge
                <InfoTooltip text="Total service charge collected in range." />
              </span>
              <FiDollarSign className="text-gray-400" />
            </div>
            <div className="text-xl font-bold text-gray-900">₹{totals.serviceCharge.toFixed(2)}</div>
            <p className="text-xs text-gray-500 mt-1">collected</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                Discounts
                <InfoTooltip text="Total discounts applied in range." />
              </span>
              <FiDollarSign className="text-gray-400" />
            </div>
            <div className="text-xl font-bold text-gray-900">₹{totals.discounts.toFixed(2)}</div>
            <p className="text-xs text-gray-500 mt-1">applied</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 flex items-center gap-1">
                Menu Items
                <InfoTooltip text="Count of active menu items." />
              </span>
              <FiGrid className="text-gray-400" />
            </div>
            <div className="text-xl font-bold text-gray-900">{menuItems.length}</div>
            <p className="text-xs text-gray-500 mt-1">active items</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 xl:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                Revenue Trend
                <InfoTooltip text="Daily revenue totals for the selected range." />
              </h3>
              <span className="text-xs text-gray-500">{startDate || 'All time'} to {endDate || 'All time'}</span>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={revenueTrend}>
                <defs>
                  <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#111827" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#111827" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 10 }} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' }}
                  formatter={(value) => `₹${parseFloat(value).toFixed(2)}`}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.date || label}
                />
                <Area type="monotone" dataKey="revenue" stroke="#111827" strokeWidth={2} fill="url(#revenueFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              Payment Mix
              <InfoTooltip text="Revenue split by payment method." />
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={paymentBreakdown} dataKey="revenue" nameKey="method" innerRadius={50} outerRadius={80}>
                  {paymentBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `₹${parseFloat(value).toFixed(2)}`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-3 space-y-2">
              {paymentBreakdown.map(item => (
                <div key={item.method} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-gray-600">{item.method}</span>
                  </div>
                  <span className="font-semibold text-gray-900">₹{item.revenue.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hourly + Top Items */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 xl:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              Hourly Revenue (IST)
              <InfoTooltip text="Total revenue by hour of day (IST)." />
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={hourlyRevenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="hour" tick={{ fill: '#6b7280', fontSize: 10 }} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                <Tooltip formatter={(value) => `₹${parseFloat(value).toFixed(2)}`} />
                <Bar dataKey="revenue" fill="#111827" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              Top Items
              <InfoTooltip text="Top selling items by revenue in range." />
            </h3>
            <div className="space-y-3">
              {topItems.length === 0 ? (
                <div className="text-xs text-gray-500">No item data in the selected range.</div>
              ) : (
                topItems.map(item => (
                  <div key={item.name} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-800">{item.name}</div>
                      <div className="text-xs text-gray-500">{item.quantity} sold</div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900">₹{item.revenue.toFixed(2)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Operational Health */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              Live Orders
              <InfoTooltip text="Current order counts by status." />
            </h3>
            {orderStatus.length === 0 ? (
              <div className="text-xs text-gray-500">No active orders</div>
            ) : (
              <div className="space-y-2">
                {orderStatus.map(status => (
                  <div key={status.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: status.color }} />
                      <span className="text-sm text-gray-600">{status.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{status.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              Table Utilization
              <InfoTooltip text="Current table status distribution." />
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Available</span>
                <span className="font-semibold text-gray-900">{tableStatus.available}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Occupied</span>
                <span className="font-semibold text-gray-900">{tableStatus.occupied}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Reserved</span>
                <span className="font-semibold text-gray-900">{tableStatus.reserved}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Cleaning</span>
                <span className="font-semibold text-gray-900">{tableStatus.cleaning}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              Reservations & Waitlist
              <InfoTooltip text="Today's reservations and current waitlist status." />
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Today’s Reservations</span>
                <span className="font-semibold text-gray-900">{reservationStats.totalToday}</span>
              </div>
              {waitlistStats && (
                <>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Waiting</span>
                    <span className="font-semibold text-gray-900">{waitlistStats.waiting || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Notified</span>
                    <span className="font-semibold text-gray-900">{waitlistStats.notified || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Seated</span>
                    <span className="font-semibold text-gray-900">{waitlistStats.seated || 0}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Feedback Analytics */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <FiStar className="text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              Guest Feedback (Selected Range)
              <InfoTooltip text="Average ratings from guest feedback in range." />
            </h3>
          </div>
          {feedbackAnalytics ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Overall Rating</div>
                <div className="text-lg font-bold text-gray-900">{feedbackAnalytics.average_ratings?.overall || 0}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Food</div>
                <div className="text-lg font-bold text-gray-900">{feedbackAnalytics.average_ratings?.food || 0}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Service</div>
                <div className="text-lg font-bold text-gray-900">{feedbackAnalytics.average_ratings?.service || 0}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Ambiance</div>
                <div className="text-lg font-bold text-gray-900">{feedbackAnalytics.average_ratings?.ambiance || 0}</div>
              </div>
            </div>
          ) : (
            <div className="text-xs text-gray-500">Feedback analytics not available.</div>
          )}
        </div>

        {/* Dining Duration + Table Turnover */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                Average Dining Duration
                <InfoTooltip text="Average minutes from order created to completed bill." />
              </h3>
              <FiClock className="text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {diningMetrics.avgDiningMinutes.toFixed(1)} min
            </div>
            <p className="text-xs text-gray-500 mt-1">{diningMetrics.diningSamples} completed bills</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                Table Turnover Time
                <InfoTooltip text="Average gap between consecutive bills on the same table." />
              </h3>
              <FiTrendingUp className="text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {diningMetrics.avgTurnoverMinutes.toFixed(1)} min
            </div>
            <p className="text-xs text-gray-500 mt-1">{diningMetrics.turnoverSamples} table cycles</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                Kitchen Avg Prep Time
                <InfoTooltip text="Average minutes from KDS display to ready." />
              </h3>
              <FiClock className="text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {kdsPrepTrend.avgPrepMinutes.toFixed(1)} min
            </div>
            <p className="text-xs text-gray-500 mt-1">{kdsPrepTrend.samples} KDS items</p>
          </div>
        </div>

        {/* Peak Hours Heatmap */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              Peak Hours Heatmap (Bills)
              <InfoTooltip text="Bill volume by day-of-week and hour (darker = busier)." />
            </h3>
            <span className="text-xs text-gray-500">Counts by day & hour (IST)</span>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[800px] relative">
              <div className="grid grid-cols-[80px_repeat(24,1fr)] gap-1 text-[10px] text-gray-500 mb-2">
                <div />
                {Array.from({ length: 24 }, (_, hour) => (
                  <div key={hour} className="text-center">{formatHourLabel(hour)}</div>
                ))}
              </div>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, dayIndex) => (
                <div key={day} className="grid grid-cols-[80px_repeat(24,1fr)] gap-1 mb-1">
                  <div className="text-xs text-gray-600 flex items-center">{day}</div>
                  {peakHeatmap.grid[dayIndex].map((value, hour) => {
                    const intensity = value / peakHeatmap.maxValue;
                    const background = `rgba(17, 24, 39, ${0.1 + intensity * 0.9})`;
                    const isHovered =
                      hoveredHeatmapCell &&
                      hoveredHeatmapCell.day === day &&
                      hoveredHeatmapCell.hour === hour;

                    return (
                      <div
                        key={`${day}-${hour}`}
                        className="relative h-6 rounded"
                        style={{ backgroundColor: value === 0 ? '#f3f4f6' : background }}
                        onMouseEnter={() => setHoveredHeatmapCell({ day, hour, value })}
                        onMouseLeave={() => setHoveredHeatmapCell(null)}
                      >
                        {isHovered && (
                          <div className="absolute z-20 -top-2 left-1/2 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md border border-gray-200 bg-white px-2 py-1 text-[10px] text-gray-700 shadow-md">
                            {day} • {formatHourLabel(hour)} • {value} bills
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Kitchen Prep Time Trend */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              Kitchen Prep Time Trend
              <InfoTooltip text="Average prep minutes per day from KDS timestamps." />
            </h3>
            <span className="text-xs text-gray-500">Average minutes from display to ready</span>
          </div>
          {kdsPrepTrend.trend.length === 0 ? (
            <div className="text-xs text-gray-500">No KDS prep data in selected range.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={kdsPrepTrend.trend}>
                <defs>
                  <linearGradient id="prepFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#111827" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#111827" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 10 }} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' }}
                  formatter={(value) => `${parseFloat(value).toFixed(1)} min`}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.date || label}
                />
                <Area type="monotone" dataKey="avgMinutes" stroke="#111827" strokeWidth={2} fill="url(#prepFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default AdvancedAnalytics;
